import { Config } from "../utils/config";
import defaultConfig from "./default/config";
import visualqueryaiConfig from "./visualqueryai/config";
import demo2Config from "./demo2/config";
import demo3Config from "./demo3/config";
import demo4Config from "./demo4/config";
import snowflakeConfig from "./snowflake/config";
import snowflakeRealcommConfig from "./snowflake_realcomm/config";
import vizQueryNextgenConfig from "./vizquery_nextgen/config";
import urbanEdgeConfig from "./urbanedge/config";
import federalRealtyConfig from "./federalrealty/config";
import uicEduConfig from "./uic_edu/config";
import insuremepConfig from "./insuremep/config";
import publicDemoConfig from "./public_demo/config";

export type DemoType =
  | "default"
  | "visualqueryai"
  | "demo2"
  | "demo3"
  | "demo4"
  | "snowflake"
  | "snowflake_realcomm"
  | "urbanedge"
  | "federalrealty"
  | "uic_edu"
  | "insuremep"
  | "vizquery_nextgen"
  | "public_demo";

export interface DemoInfo {
  id: DemoType;
  name: string;
  description: string;
  config: Config;
  route: string;
}

// Available demo definitions
export const DEMOS: Record<DemoType, DemoInfo> = {
  default: {
    id: "default",
    name: "CADViewer Visual Query",
    description:
      "CADViewer configuration with full feature set, linked against CADViewer Conversion Server",
    config: defaultConfig,
    route: "/",
  },
  visualqueryai: {
    id: "visualqueryai",
    name: "Visual Query - Shopping Mall with AI Integration",
    description:
      "Visual Query CADViewer AI Assistant facilitate spatial queries over floorplans and business data for Real Estate owners and operators.",
    config: visualqueryaiConfig,
    route: "/visualqueryai",
  },
  demo2: {
    id: "demo2",
    name: "RealCOMM Savannah Demo 2025",
    description: "RealCOMM Savannah - Customer Demo ",
    config: demo2Config,
    route: "/demo2",
  },
  demo3: {
    id: "demo3",
    name: "CADViewer - View and interact with DWG, DGN, TIFF and CALS files",
    description:
      "Demo to illustrate CADViewer capabilities with a selection of engineering vector and bitmap files",
    config: demo3Config,
    route: "/demo3",
  },
  demo4: {
    id: "demo4",
    name: "Demo 4",
    description: "Template for future demo",
    config: demo4Config,
    route: "/demo4",
  },
  snowflake: {
    id: "snowflake",
    name: "Visual Query - Snowflake Integration",
    description:
      "Visual Query CADViewer integrated with Snowflake-backed property and space metadata.",
    config: snowflakeConfig,
    route: "/snowflake",
  },
  snowflake_realcomm: {
    id: "snowflake_realcomm",
    name: "Visual Query - Snowflake Integration- REALCOMM dataset",
    description:
      "Visual Query CADViewer integrated with Snowflake-backed property and space metadata.",
    config: snowflakeRealcommConfig,
    route: "/snowflake_realcomm",
  },
  urbanedge: {
    id: "urbanedge",
    name: "Visual Query - UrbanEdge sample",
    description:
      "Visual Query CADViewer with sample UrbanEdge information set.",
    config: urbanEdgeConfig,
    route: "/urbanedge",
  },
  federalrealty: {
    id: "federalrealty",
    name: "Visual Query - Federal Realty sample",
    description:
      "Visual Query CADViewer with sample Federal Realty information set.",
    config: federalRealtyConfig,
    route: "/federalrealty",
  },
  uic_edu: {
    id: "uic_edu",
    name: "Visual Query - University of Illinois - Chicago sample",
    description:
      "Visual Query CADViewer with sample uic.edu information set.",
    config: uicEduConfig,
    route: "/uic_edu",
  },
  insuremep: {
    id: "insuremep",
    name: "Visual Query - Insuremep/ibaseit sample",
    description:
      "Visual Query CADViewer with sample Insuremep/ibaseit information set.",
    config: insuremepConfig,
    route: "/insuremep",
  },
  vizquery_nextgen: {
    id: "vizquery_nextgen",
    name: "Visual Query - NextGen AI - CADViewer Analytic Model",
    description:
      "Visual Query CADViewer integrated with AI - CADViewer Analytic Model.",
    config: vizQueryNextgenConfig,
    route: "/vizquery_nextgen",
  },
  public_demo: {
    id: "public_demo",
    name: "Visual Query - Public Demo",
    description:
      "Public-facing Visual Query demo with Snowflake AI integration.",
    config: publicDemoConfig,
    route: "/public_demo",
  },

};

/**
 * Detects the active demo from the current route
 */
export function getActiveDemoFromRoute(): DemoType {
  const pathname = window.location.pathname;

  // 1) Exact route match
  for (const demo of Object.values(DEMOS)) {
    if (demo.route === pathname) {
      return demo.id;
    }
  }

  // 2) Sub-page match (e.g. "/public_demo/distance-query" -> public_demo).
  //    Longest route first so the most specific demo wins; skip "/" which
  //    would otherwise prefix-match every path.
  const byRouteLength = Object.values(DEMOS)
    .filter((demo) => demo.route !== "/")
    .sort((a, b) => b.route.length - a.route.length);
  for (const demo of byRouteLength) {
    if (pathname.startsWith(demo.route + "/")) {
      return demo.id;
    }
  }

  return "default";
}

/**
 * Gets the active configuration based on the current route
 */
export function getActiveConfig() {
  const activeDemo = getActiveDemoFromRoute();
  return DEMOS[activeDemo].config;
}

/**
 * Gets the active demo information
 */
export function getActiveDemoInfo(): DemoInfo {
  const activeDemo = getActiveDemoFromRoute();
  return DEMOS[activeDemo];
}

/**
 * Gets the list of all available demos
 */
export function getAllDemos(): DemoInfo[] {
  return Object.values(DEMOS);
}

/**
 * Gets the route for a given demo
 */
export function getDemoRoute(demoType: DemoType): string {
  return DEMOS[demoType]?.route || "/";
}
